'use client';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import { useNotification } from '@/contexts/NotificationContext';
import {
  batchUpdateTomVendingInventory,
  queryTomVendingBrands,
  queryTomVendingCategories,
  queryTomVendingInventory,
  type TomVendingInventoryBatchUpdateRow,
  type TomVendingInventoryRow,
  type TvBrand,
  type TvCategory,
} from '@/hooks/useTomVendingInventory';
import { queryTomVendingVendors, type TvVendor } from '@/hooks/useTomVendingPurchasing';

type EditableFields = {
  brandId: number | null;
  flavor: string;
  weightText: string;
  categoryId: number | null;
  schoolApproved: boolean;
  priceStr: string;
  costStr: string;
  internalNotes: string;
  preferredVendorId: number | null;
};

function centsToInputStr(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
}

function parseMoneyInputToCents(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (Number.isNaN(n) || n < 0) {
    throw new Error('INVALID_MONEY');
  }
  return Math.round(n * 100);
}

function rowToEditable(row: TomVendingInventoryRow): EditableFields {
  return {
    brandId: row.brandId ?? null,
    flavor: row.flavor ?? '',
    weightText: row.weightText ?? '',
    categoryId: row.categoryId ?? null,
    schoolApproved: row.schoolApproved ?? false,
    priceStr: centsToInputStr(row.priceCents),
    costStr: centsToInputStr(row.costCents),
    internalNotes: row.internalNotes ?? '',
    preferredVendorId: row.preferredVendorId ?? null,
  };
}

function editableEquals(a: EditableFields, b: EditableFields): boolean {
  return (
    a.brandId === b.brandId &&
    a.flavor === b.flavor &&
    a.weightText === b.weightText &&
    a.categoryId === b.categoryId &&
    a.schoolApproved === b.schoolApproved &&
    a.priceStr === b.priceStr &&
    a.costStr === b.costStr &&
    a.internalNotes === b.internalNotes &&
    a.preferredVendorId === b.preferredVendorId
  );
}

function buildBatchRow(id: number, edited: EditableFields): TomVendingInventoryBatchUpdateRow {
  const priceCents = parseMoneyInputToCents(edited.priceStr);
  const costCents = parseMoneyInputToCents(edited.costStr);
  return {
    id,
    brandId: edited.brandId,
    flavor: edited.flavor.trim() || null,
    weightText: edited.weightText.trim() || null,
    categoryId: edited.categoryId,
    schoolApproved: edited.schoolApproved,
    priceCents,
    costCents,
    internalNotes: edited.internalNotes.trim() || null,
    preferredVendorId: edited.preferredVendorId,
  };
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];

/** Tighter layout for the quick-edit grid only — more columns visible without horizontal scroll. */
const COMPACT_TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    px: 0.5,
    py: 0.625,
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    px: 0.5,
    py: 0.75,
    fontSize: '0.7rem',
    lineHeight: 1.2,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
  },
  '& .MuiInputBase-input': {
    py: 0.375,
    px: 0.5,
  },
  '& .MuiSelect-select': {
    py: 0.375,
    px: 0.5,
    minHeight: 'auto',
  },
  '& .MuiCheckbox-root': {
    p: 0,
  },
};

const compactFieldSx = { minWidth: 0, width: '100%' };

const TomsVendingCatalogQuickEditPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<TomVendingInventoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [originalById, setOriginalById] = useState<Record<number, EditableFields>>({});
  const [editedById, setEditedById] = useState<Record<number, EditableFields>>({});
  const [missingPriceOrCost, setMissingPriceOrCost] = useState(false);
  const [brands, setBrands] = useState<TvBrand[]>([]);
  const [categories, setCategories] = useState<TvCategory[]>([]);
  const [vendors, setVendors] = useState<TvVendor[]>([]);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    void queryTomVendingBrands('', { includeInactive: false }).then(setBrands).catch(() => setBrands([]));
    void queryTomVendingCategories('', { includeInactive: false }).then(setCategories).catch(() => setCategories([]));
    void queryTomVendingVendors('', { includeInactive: false }).then(setVendors).catch(() => setVendors([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await queryTomVendingInventory({
        skip: page * rowsPerPage,
        take: rowsPerPage,
        search: debouncedSearch,
        orderBy: 'upc',
        orderDirection: 'ASC',
        includeSignedUrls: false,
        includeVariants: false,
      });
      setRows(r.data);
      setTotal(r.total);
      const orig: Record<number, EditableFields> = {};
      const edit: Record<number, EditableFields> = {};
      for (const row of r.data) {
        const e = rowToEditable(row);
        orig[row.id] = e;
        edit[row.id] = { ...e };
      }
      setOriginalById(orig);
      setEditedById(edit);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load catalog');
      setRows([]);
      setTotal(0);
      setOriginalById({});
      setEditedById({});
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirtyIds = useMemo(() => {
    const ids: number[] = [];
    for (const id of Object.keys(editedById)) {
      const numId = Number(id);
      const orig = originalById[numId];
      const edited = editedById[numId];
      if (orig && edited && !editableEquals(orig, edited)) {
        ids.push(numId);
      }
    }
    return ids;
  }, [editedById, originalById]);

  const displayRows = useMemo(() => {
    if (!missingPriceOrCost) return rows;
    return rows.filter((r) => r.priceCents == null || r.costCents == null);
  }, [rows, missingPriceOrCost]);

  const updateField = (id: number, patch: Partial<EditableFields>) => {
    setEditedById((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const handleSave = async () => {
    if (dirtyIds.length === 0) return;
    setSaving(true);
    try {
      const batchRows: TomVendingInventoryBatchUpdateRow[] = [];
      for (const id of dirtyIds) {
        const edited = editedById[id];
        if (!edited) continue;
        try {
          batchRows.push(buildBatchRow(id, edited));
        } catch {
          showError(`Invalid price or cost on item #${id}`);
          setSaving(false);
          return;
        }
      }
      const result = await batchUpdateTomVendingInventory({
        rows: batchRows,
        monetaryChangeNote: 'Catalog quick edit',
      });
      if (result.failed.length > 0) {
        const msg = result.failed
          .slice(0, 5)
          .map((f) => `#${f.id}: ${f.error}`)
          .join('; ');
        showError(`Saved ${result.updated}; failed: ${msg}${result.failed.length > 5 ? '…' : ''}`);
      } else {
        showSuccess(`Saved ${result.updated} item(s)`);
      }
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Catalog', href: '/app/toms-vending/inventory' },
    { label: 'Quick edit' },
  ];

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title="Catalog quick edit"
        rightContent={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button component={RouterLink} to="/app/toms-vending/inventory" variant="outlined" sx={{ textTransform: 'none' }}>
              Full catalog
            </Button>
            <Button
              variant="contained"
              disabled={saving || dirtyIds.length === 0}
              onClick={() => void handleSave()}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : `Save changes (${dirtyIds.length})`}
            </Button>
          </Box>
        }
      />

      <Box sx={{ px: 0.5, py: 1, maxWidth: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
          Edit price, cost, and merchandising fields inline. Changes are saved when you click Save changes. Product
          descriptions from Open Food Facts stay on the full catalog page.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <TextField
            placeholder="Search UPC, name, brand…"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ width: 280 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={missingPriceOrCost}
                onChange={(e) => setMissingPriceOrCost(e.target.checked)}
                size="small"
              />
            }
            label="Missing price or cost (this page)"
          />
        </Box>

        <Box sx={{ mx: { xs: -0.5, sm: -2 }, maxWidth: { sm: 'calc(100% + 32px)' } }}>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader sx={COMPACT_TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '9%' }}>UPC</TableCell>
                <TableCell sx={{ width: '11%' }}>Name</TableCell>
                <TableCell sx={{ width: '9%' }}>Brand</TableCell>
                <TableCell sx={{ width: '9%' }}>Flavor</TableCell>
                <TableCell sx={{ width: '6%' }}>Wt</TableCell>
                <TableCell sx={{ width: '9%' }}>Cat</TableCell>
                <TableCell sx={{ width: '4%', textAlign: 'center' }}>Sch</TableCell>
                <TableCell sx={{ width: '6%' }}>Price</TableCell>
                <TableCell sx={{ width: '6%' }}>Cost</TableCell>
                <TableCell sx={{ width: '9%' }}>Vendor</TableCell>
                <TableCell sx={{ width: '12%' }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : displayRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    No items
                  </TableCell>
                </TableRow>
              ) : (
                displayRows.map((row) => {
                  const edited = editedById[row.id] ?? rowToEditable(row);
                  const isDirty = originalById[row.id] && !editableEquals(originalById[row.id], edited);
                  return (
                    <TableRow key={row.id} sx={{ bgcolor: isDirty ? 'action.selected' : undefined }}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.2 }}
                        >
                          {row.upc}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {row.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={edited.brandId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateField(row.id, {
                              brandId: v === '' ? null : Number(v),
                            });
                          }}
                          displayEmpty
                          sx={compactFieldSx}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {brands.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                              {b.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={edited.flavor}
                          onChange={(e) => updateField(row.id, { flavor: e.target.value })}
                          sx={compactFieldSx}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={edited.weightText}
                          onChange={(e) => updateField(row.id, { weightText: e.target.value })}
                          sx={compactFieldSx}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={edited.categoryId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateField(row.id, {
                              categoryId: v === '' ? null : Number(v),
                            });
                          }}
                          displayEmpty
                          sx={compactFieldSx}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {categories.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={edited.schoolApproved}
                          onChange={(e) => updateField(row.id, { schoolApproved: e.target.checked })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={edited.priceStr}
                          onChange={(e) => updateField(row.id, { priceStr: e.target.value })}
                          placeholder="—"
                          sx={compactFieldSx}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={edited.costStr}
                          onChange={(e) => updateField(row.id, { costStr: e.target.value })}
                          placeholder="—"
                          sx={compactFieldSx}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={edited.preferredVendorId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateField(row.id, {
                              preferredVendorId: v === '' ? null : Number(v),
                            });
                          }}
                          displayEmpty
                          sx={compactFieldSx}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {vendors.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                              {v.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={edited.internalNotes}
                          onChange={(e) => updateField(row.id, { internalNotes: e.target.value })}
                          sx={compactFieldSx}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{
            '& .MuiTablePagination-toolbar': { px: 0.5, minHeight: 44, py: 0.5 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
            },
          }}
        />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Manage{' '}
          <MuiLink component={RouterLink} to="/app/toms-vending/inventory/brands">
            brands
          </MuiLink>
          ,{' '}
          <MuiLink component={RouterLink} to="/app/toms-vending/inventory/categories">
            categories
          </MuiLink>
          , and{' '}
          <MuiLink component={RouterLink} to="/app/toms-vending/purchasing/vendors">
            vendors
          </MuiLink>
          .
        </Typography>
      </Box>
    </>
  );
};

export default TomsVendingCatalogQuickEditPage;
